import { supabase } from '../../lib/supabaseClient';
import { useAuthStore } from '../../store/useAuthStore';
import type { Tables, TablesInsert, TablesUpdate } from '../../types/database.types';
import { withDefaultSelfAssignee } from '../../lib/tasks/taskAssignmentDefaults';
import { assignTask } from './taskCollaborationService';

// ============================================================
// Types
// ============================================================

export type Task = Tables<'tasks'>;
export type TaskInsert = TablesInsert<'tasks'>;
export type TaskUpdate = TablesUpdate<'tasks'>;
export type TaskSection = Tables<'task_sections'>;

export type RecurrenceType = 'daily' | 'weekly' | 'monthly' | 'yearly';

export interface RecurrenceConfig {
  type: RecurrenceType;
  interval: number;
  daysOfWeek?: number[];
  dayOfMonth?: number;
  endDate?: string;
  count?: number;
}

export interface CreateTaskInput {
  content: string;
  description?: string | null;
  priority?: number;
  labels?: string[];
  due_date?: string | null;
  due_datetime?: string | null;
  deadline?: string | null;
  dossier_id?: string | null;
  section_id?: string | null;
  estimated_duration_minutes?: number | null;
  parent_id?: string | null;
  recurrence?: RecurrenceConfig;
  /** Si omis, le créateur est auto-assigné. Tableau vide = personne. */
  assigneeIds?: string[];
  /** Tâche type dont la tâche vient : c'est son type, il donne sa couleur. */
  task_template_id?: string | null;
}

export interface TaskFilters {
  dossier_id?: string;
  completed?: boolean;
  section_id?: string | null;
}

// Hierarchical view (subtasks)
export interface TaskWithChildren extends Task {
  children?: TaskWithChildren[];
}

// ============================================================
// Helpers
// ============================================================

function getCurrentCollaboratorId(): string {
  const id = useAuthStore.getState().collaborator?.id;
  if (!id) {
    throw new Error('Not authenticated — cannot perform task operation');
  }
  return id;
}

// ============================================================
// CRUD — Tasks
// ============================================================

export async function createTask(input: CreateTaskInput): Promise<Task> {
  // Pas besoin du collaboratorId côté client : la RPC le récupère via auth.uid().
  // (La RPC bypass RLS proprement et calcule depth + task_order côté serveur.)
  // La récurrence sera ajoutée plus tard via updateTask si besoin.
  const { data, error } = await supabase.rpc('create_task', {
    p_content: input.content,
    p_dossier_id: input.dossier_id ?? undefined,
    p_section_id: input.section_id ?? undefined,
    p_parent_id: input.parent_id ?? undefined,
    p_priority: input.priority ?? 3,
    p_description: input.description ?? undefined,
    p_due_date: input.due_date ?? undefined,
    p_due_datetime: input.due_datetime ?? undefined,
    p_deadline: input.deadline ?? undefined,
    p_estimated_duration_minutes: input.estimated_duration_minutes ?? undefined,
    p_labels: input.labels ?? [],
  });

  if (error) throw new Error(`Failed to create task: ${error.message}`);
  if (!data) throw new Error('create_task returned no data');

  let task = data as Task;

  // La RPC ne connaît pas la tâche type : on la pose juste après. Non bloquant,
  // la tâche existe déjà et resterait seulement sans couleur.
  if (input.task_template_id) {
    const typed = await supabase
      .from('tasks')
      .update({ task_template_id: input.task_template_id })
      .eq('id', task.id)
      .select()
      .single();
    if (!typed.error && typed.data) task = typed.data;
  }

  // Si récurrence demandée, on patch via updateTask
  if (input.recurrence) {
    task = await updateTask(task.id, {
      is_recurring: true,
      recurrence_type: input.recurrence.type,
      recurrence_interval: input.recurrence.interval,
      recurrence_days_of_week: input.recurrence.daysOfWeek ?? null,
      recurrence_day_of_month: input.recurrence.dayOfMonth ?? null,
      recurrence_end_date: input.recurrence.endDate ?? null,
      recurrence_count: input.recurrence.count ?? null,
    });
  }

  const selfId = useAuthStore.getState().collaborator?.id ?? null;
  const assigneeIds = input.assigneeIds ?? withDefaultSelfAssignee([], selfId);
  for (const id of assigneeIds) {
    try {
      await assignTask(task.id, id);
    } catch {
      // La tâche existe ; l’assignation par défaut n’est pas bloquante.
    }
  }

  return task;
}

export async function getTasks(filters?: TaskFilters): Promise<Task[]> {
  let query = supabase
    .from('tasks')
    .select('*')
    .eq('is_deleted', false)
    .order('section_id', { ascending: true, nullsFirst: true })
    .order('task_order', { ascending: true })
    .order('created_at', { ascending: false });

  if (filters?.dossier_id !== undefined) {
    query = query.eq('dossier_id', filters.dossier_id);
  }
  if (filters?.completed !== undefined) {
    query = query.eq('is_completed', filters.completed);
  }
  if (filters?.section_id !== undefined) {
    if (filters.section_id === null) {
      query = query.is('section_id', null);
    } else {
      query = query.eq('section_id', filters.section_id);
    }
  }

  const { data, error } = await query;
  if (error) throw new Error(`Failed to fetch tasks: ${error.message}`);
  return data ?? [];
}

export async function getTask(taskId: string): Promise<Task | null> {
  const { data, error } = await supabase
    .from('tasks')
    .select('*')
    .eq('id', taskId)
    .maybeSingle();
  if (error) throw new Error(`Failed to fetch task: ${error.message}`);
  return data;
}

export async function updateTask(taskId: string, updates: TaskUpdate): Promise<Task> {
  const { data, error } = await supabase
    .from('tasks')
    .update(updates)
    .eq('id', taskId)
    .select()
    .single();
  if (error) throw new Error(`Failed to update task: ${error.message}`);
  return data;
}

export async function completeTask(taskId: string): Promise<Task> {
  const collaboratorId = getCurrentCollaboratorId();
  return updateTask(taskId, {
    is_completed: true,
    completed_at: new Date().toISOString(),
    completed_by: collaboratorId,
  });
}

export async function uncompleteTask(taskId: string): Promise<Task> {
  return updateTask(taskId, {
    is_completed: false,
    completed_at: null,
    completed_by: null,
  });
}

export async function deleteTask(taskId: string): Promise<void> {
  const { error } = await supabase
    .from('tasks')
    .update({
      is_deleted: true,
      deleted_at: new Date().toISOString(),
    })
    .eq('id', taskId);
  if (error) throw new Error(`Failed to delete task: ${error.message}`);
}

// ============================================================
// Archives
// ============================================================

export interface ArchivedTaskFilters {
  dossierId?: string | null;
  search?: string;
  /** Date min (created_at >=) */
  from?: string;
  /** Date max (created_at <=) */
  to?: string;
}

export async function getArchivedTasks(filters?: ArchivedTaskFilters): Promise<Task[]> {
  let q = supabase
    .from('tasks')
    .select('*')
    .eq('is_deleted', true)
    .order('deleted_at', { ascending: false });

  if (filters?.dossierId !== undefined) {
    if (filters.dossierId === null) {
      q = q.is('dossier_id', null);
    } else {
      q = q.eq('dossier_id', filters.dossierId);
    }
  }
  if (filters?.search && filters.search.trim()) {
    const term = filters.search.trim().replace(/[%_]/g, '\\$&');
    q = q.ilike('content', `%${term}%`);
  }
  if (filters?.from) {
    q = q.gte('deleted_at', filters.from);
  }
  if (filters?.to) {
    q = q.lte('deleted_at', filters.to);
  }

  const { data, error } = await q;
  if (error) throw new Error(`Failed to fetch archived tasks: ${error.message}`);
  return data ?? [];
}

export async function restoreTask(taskId: string): Promise<Task> {
  const { data, error } = await supabase.rpc('restore_task', { p_task_id: taskId });
  if (error) throw new Error(`Failed to restore task: ${error.message}`);
  if (!data) throw new Error('restore_task returned no data');
  return data as Task;
}

// ============================================================
// Sections
// ============================================================

export async function getSections(dossierId: string | null): Promise<TaskSection[]> {
  let query = supabase
    .from('task_sections')
    .select('*')
    .eq('is_archived', false)
    .order('section_order', { ascending: true });

  if (dossierId === null) {
    query = query.is('dossier_id', null);
  } else {
    query = query.eq('dossier_id', dossierId);
  }

  const { data, error } = await query;
  if (error) throw new Error(`Failed to fetch sections: ${error.message}`);
  return data ?? [];
}

export interface CreateSectionInput {
  name: string;
  color?: string | null;
  dossier_id?: string | null;
}

export async function createSection(input: CreateSectionInput): Promise<TaskSection> {
  const collaboratorId = getCurrentCollaboratorId();

  // Append at end (builder Supabase immutable → on réassigne)
  let orderQuery = supabase
    .from('task_sections')
    .select('section_order')
    .eq('is_archived', false)
    .order('section_order', { ascending: false })
    .limit(1);
  if (input.dossier_id) {
    orderQuery = orderQuery.eq('dossier_id', input.dossier_id);
  } else {
    orderQuery = orderQuery.is('dossier_id', null);
  }
  const { data: existing } = await orderQuery;
  const maxOrder = existing?.[0]?.section_order ?? -1;

  const { data, error } = await supabase
    .from('task_sections')
    .insert({
      name: input.name,
      color: input.color ?? null,
      dossier_id: input.dossier_id ?? null,
      created_by: collaboratorId,
      section_order: maxOrder + 1,
    })
    .select()
    .single();

  if (error) throw new Error(`Failed to create section: ${error.message}`);
  return data;
}

export async function reorderSections(
  dossierId: string,
  sectionIds: string[],
): Promise<void> {
  const { error } = await supabase.rpc('reorder_sections', {
    p_dossier_id: dossierId,
    p_section_ids: sectionIds,
  });
  if (error) throw new Error(`Failed to reorder sections: ${error.message}`);
}

export async function moveTask(params: {
  taskId: string;
  targetSectionId: string | null;
  beforeTaskId?: string | null;
}): Promise<void> {
  const { error } = await supabase.rpc('move_task', {
    p_task_id: params.taskId,
    // RPC accepte uuid ou null — Supabase JS gère null pour uuid
    p_target_section_id: (params.targetSectionId ?? null) as string,
    p_before_task_id: (params.beforeTaskId ?? null) as string | undefined,
  });
  if (error) throw new Error(`Failed to move task: ${error.message}`);
}

export async function archiveSection(sectionId: string): Promise<void> {
  const { error } = await supabase
    .from('task_sections')
    .update({ is_archived: true })
    .eq('id', sectionId);
  if (error) throw new Error(`Failed to archive section: ${error.message}`);
}

// ============================================================
// Tree helpers
// ============================================================

export function buildTaskTree(tasks: Task[]): TaskWithChildren[] {
  const byId = new Map<string, TaskWithChildren>();
  const roots: TaskWithChildren[] = [];

  for (const t of tasks) {
    byId.set(t.id, { ...t, children: [] });
  }
  for (const t of tasks) {
    const node = byId.get(t.id)!;
    if (t.parent_id && byId.has(t.parent_id)) {
      byId.get(t.parent_id)!.children!.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

// ============================================================
// Dashboard helpers
// ============================================================

/**
 * Tâches ouvertes assignées à un collaborateur, triées par urgence
 * (priority asc, puis deadline asc, puis due_date asc).
 * `limit` optionnel : sans limite, tout le jeu trié.
 */
export async function getMyOpenTasks(
  collaboratorId: string,
  limit?: number,
): Promise<Task[]> {
  const { data, error } = await supabase
    .from('task_assignments')
    .select('task:tasks!inner(*)')
    .eq('assignee_id', collaboratorId);
  if (error) throw new Error(`Failed to fetch my tasks: ${error.message}`);

  const tasks = (data ?? [])
    .map((r) => (r as unknown as { task: Task | null }).task)
    .filter((t): t is Task => !!t && !t.is_completed && !t.is_deleted);

  // Sort : priority asc (1=urgent), then earliest deadline, then earliest due
  tasks.sort((a, b) => {
    if (a.priority !== b.priority) return a.priority - b.priority;
    const aDeadline = a.deadline ?? a.due_date ?? '9999-12-31';
    const bDeadline = b.deadline ?? b.due_date ?? '9999-12-31';
    return aDeadline.localeCompare(bDeadline);
  });

  return typeof limit === 'number' ? tasks.slice(0, limit) : tasks;
}
